import React, { useEffect, useRef, useState } from 'react';
import { createBeefreeExportBridge, createRevisionTracker, loadBeefreePlugin, parseBeefreeExport } from './beefreeSdk';
import { beefreeStarterTemplate } from './beefreeStarterTemplate';
import BeefreeAssetsPanel from './BeefreeAssetsPanel';
import BeefreeLeaveDialog from './BeefreeLeaveDialog';
import { enableBeefreeLocalImages } from './beefreeLocalImages';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
});
const hasUnsubscribe = (html) => /href\s*=\s*["']\s*\{\{\s*unsubscribe_url\s*\}\}\s*["']/i.test(html || '');

const requestJson = async (url, options, signal) => {
  const response = await fetch(url, { credentials: 'same-origin', ...options, signal });
  let data;
  try { data = await response.json(); } catch (_) { throw new Error('The server returned an unexpected response. Check your sign-in and connection.'); }
  if (!response.ok) throw new Error(data.error || data.errors?.join('. ') || 'The request could not be completed.');
  return data;
};

const BeefreeEmailEditor = ({ template, senders = [], beefreeLocalImages = false, onPersisted, previewComponent: PreviewFrame, testSendComponent: TestSend }) => {
  const containerRef = useRef(null);
  const operationFocusRef = useRef(null);
  const canvasMayHaveChangesRef = useRef(false);
  const hasLoadedCanvasRef = useRef(false);
  const instanceRef = useRef(null);
  const bridgeRef = useRef(null);
  const aliveRef = useRef(false);
  const busyRef = useRef(false);
  const nativeSaveRef = useRef(null);
  const pendingLeaveRef = useRef(null);
  const allowDepartureRef = useRef(false);
  // A pristine new template has nothing to lose until the user starts editing.
  const revisionsRef = useRef(createRevisionTracker(true));
  const [id, setId] = useState(template?.id || null);
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [preheader, setPreheader] = useState(template?.preheader || '');
  const [dirty, setDirty] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [mode, setMode] = useState('visual');
  const [device, setDevice] = useState('desktop');
  const [html, setHtml] = useState(template?.html || '');
  const [exportRevision, setExportRevision] = useState(template?.id ? 0 : -1);
  const [leaveDestination, setLeaveDestination] = useState(null);

  const changed = () => {
    revisionsRef.current.change();
    setDirty(true);
    setNotice('');
  };

  useEffect(() => {
    aliveRef.current = true;
    hasLoadedCanvasRef.current = false;
    let cancelled = false;
    let sessionFailed = false;
    let instance;
    const container = containerRef.current;
    const restoreLocalImages = enableBeefreeLocalImages(container, beefreeLocalImages);
    const authController = new AbortController();
    const bridge = createBeefreeExportBridge(() => {
      if (!instanceRef.current) throw new Error('Editor not ready');
      return instanceRef.current.save();
    });
    bridgeRef.current = bridge;
    const fail = (message) => {
      if (cancelled || !aliveRef.current) return;
      sessionFailed = true;
      clearTimeout(loadTimer);
      bridge.fail(message);
      setReady(false);
      setError(message);
    };
    const loadTimer = setTimeout(() => {
      authController.abort();
      fail('Beefree did not finish loading. Check SDK credentials and network access.');
    }, 45000);

    const start = async () => {
      try {
        const token = await requestJson('/email_templates/beefree_token', { method: 'POST', headers: jsonHeaders(), body: '{}' }, authController.signal);
        if (cancelled || !aliveRef.current || sessionFailed) return;
        if (!token.access_token || token.v2 !== true || !token.uid) throw new Error('The server returned an invalid editor session.');
        const plugin = await loadBeefreePlugin();
        if (cancelled || !aliveRef.current || sessionFailed) return;
        const config = {
          container: container.id,
          uid: token.uid,
          language: 'en-US',
          autosave: false,
          specialLinks: [{ type: 'Retention', label: 'Unsubscribe', link: '{{unsubscribe_url}}' }],
          onLoad: () => {
            if (cancelled || !aliveRef.current || sessionFailed) return;
            hasLoadedCanvasRef.current = true;
            clearTimeout(loadTimer);
            setReady(true);
          },
          onSave: (jsonFile, htmlFile) => {
            if (cancelled || !aliveRef.current) return;
            if (!bridge.onSave(jsonFile, htmlFile) && !busyRef.current) nativeSaveRef.current?.(jsonFile, htmlFile);
          },
          onError: (details) => {
            if (cancelled || !aliveRef.current) return;
            // A token refresh can emit 401 while the SDK recovers automatically.
            // Never display the raw vendor payload: it can include session data.
            if (Number(details?.code) === 401) {
              setNotice('Beefree is refreshing its connection. Keep this page open.');
              return;
            }
            fail('The Beefree session encountered an error. Keep this page open; unsaved changes have not been saved.');
          }
        };
        // Keep credentials on the server; only the short-lived session is passed to SDK.
        plugin.create({ access_token: token.access_token, v2: true }, config, (created) => {
          if (cancelled || !aliveRef.current || sessionFailed) {
            try { if (typeof created?.destroy === 'function') created.destroy(); } catch (_) { /* The closed component cannot recover a vendor cleanup failure. */ }
            return;
          }
          if (typeof created?.start !== 'function') {
            fail('Beefree did not provide a usable editor session.');
            return;
          }
          instance = created;
          instanceRef.current = created;
          try {
            const result = created.start(template?.design || beefreeStarterTemplate());
            if (result?.catch) result.catch(() => fail('Beefree could not open this design. The saved template has not been changed.'));
          } catch (_) { fail('Beefree could not open this design. The saved template has not been changed.'); }
        });
      } catch (e) {
        fail(e.name === 'AbortError' ? 'Beefree authorization timed out.' : e.message);
      }
    };
    start();

    return () => {
      cancelled = true;
      aliveRef.current = false;
      hasLoadedCanvasRef.current = false;
      authController.abort();
      clearTimeout(loadTimer);
      bridge.dispose();
      instanceRef.current = null;
      try { if (typeof instance?.destroy === 'function') instance.destroy(); } catch (_) { /* Always remove the iframe, even if SDK teardown fails. */ }
      restoreLocalImages();
      container.textContent = '';
    };
  }, []); // The native document is loaded once; never replace an unsaved canvas on re-render.

  useEffect(() => {
    let focusTimer;
    // Free has no onChange event. Entering the cross-origin editor can mean an
    // edit, so protect that session conservatively until our server confirms Save.
    // Do not read the iframe DOM or depend on undocumented SDK messages.
    const inspectEditorFocus = () => {
      const focused = document.activeElement;
      if (!hasLoadedCanvasRef.current || busyRef.current || focused?.tagName !== 'IFRAME' || !containerRef.current?.contains(focused)) return;
      canvasMayHaveChangesRef.current = true;
      setDirty(true);
      setNotice('');
    };
    const deferFocusCheck = () => {
      clearTimeout(focusTimer);
      if (busyRef.current) return;
      focusTimer = setTimeout(inspectEditorFocus, 0);
    };
    const needsProtection = () => busyRef.current || canvasMayHaveChangesRef.current || revisionsRef.current.dirty();
    const unload = (event) => {
      if (allowDepartureRef.current) {
        allowDepartureRef.current = false;
        return;
      }
      inspectEditorFocus();
      if (!needsProtection()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const requestDeparture = (event, url) => {
      inspectEditorFocus();
      if (!needsProtection()) return;
      if (!url) return;
      let destination;
      try { destination = new URL(url, window.location.href); } catch (_) { return; }
      if (destination.origin !== window.location.origin || !/^https?:$/.test(destination.protocol)) return;
      event.preventDefault();
      if (!pendingLeaveRef.current) {
        pendingLeaveRef.current = destination.href;
        setLeaveDestination(destination.href);
      }
    };
    const visit = (event) => {
      if (!event.defaultPrevented) requestDeparture(event, event.data?.url);
    };
    const click = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self') || link.hasAttribute('data-method') || link.hasAttribute('data-confirm')) return;
      const destination = new URL(link.href, window.location.href);
      // In-page anchors and new tabs do not abandon the current email.
      if (destination.pathname === window.location.pathname && destination.search === window.location.search && destination.hash) return;
      requestDeparture(event, destination.href);
      // Stop Turbolinks/UJS from processing a click already held by our dialog.
      if (event.defaultPrevented) event.stopImmediatePropagation();
    };
    window.addEventListener('blur', deferFocusCheck);
    const container = containerRef.current;
    container.addEventListener('focusin', inspectEditorFocus);
    if (dirty || busy) window.addEventListener('beforeunload', unload);
    document.addEventListener('click', click, true);
    document.addEventListener('turbolinks:before-visit', visit);
    return () => {
      clearTimeout(focusTimer);
      window.removeEventListener('blur', deferFocusCheck);
      container.removeEventListener('focusin', inspectEditorFocus);
      window.removeEventListener('beforeunload', unload);
      document.removeEventListener('click', click, true);
      document.removeEventListener('turbolinks:before-visit', visit);
    };
  }, [dirty, busy]);

  const exportFor = async (target, providedOutput) => {
    if (!ready || busyRef.current) return null;
    if (target === 'save' && (!name.trim() || !subject.trim())) {
      setError('Name and Subject are required.');
      return null;
    }
    busyRef.current = true;
    // Free has no change-tracking callback. Freeze the canvas while exporting
    // and persisting, then always export again before a test send.
    const canvas = containerRef.current;
    canvas.inert = true;
    canvas.style.pointerEvents = 'none';
    operationFocusRef.current?.focus({ preventScroll: true });
    setBusy(true);
    setError('');
    setNotice('');
    const revision = revisionsRef.current.snapshot();
    const fields = { name, subject, preheader };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 60000);
    try {
      const output = providedOutput || await bridgeRef.current.request();
      if (!aliveRef.current) return null;
      setHtml(output.html);
      setExportRevision(revision);
      if (target !== 'save') {
        setMode(target);
        return null;
      }
      const data = await requestJson(id ? `/email_templates/${id}` : '/email_templates', {
        method: id ? 'PATCH' : 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ email_template: { ...fields, html: output.html, design: output.design, editor_type: 'beefree' } })
      }, controller.signal);
      if (!aliveRef.current) return null;
      if (!id) window.history.replaceState(null, '', `/email_templates/${data.id}/edit`);
      setId(data.id);
      onPersisted?.();
      const stillDirty = revisionsRef.current.saved(revision);
      canvasMayHaveChangesRef.current = false;
      setDirty(stillDirty);
      setNotice(stillDirty ? 'Saved snapshot. Newer changes still need to be saved.' : data.sendable ? 'Email saved.' : 'Saved. Add an Unsubscribe link before using this email in a campaign.');
      return data.id;
    } catch (e) {
      if (aliveRef.current) setError(e.name === 'AbortError' ? 'Saving timed out. Check the template list before retrying; the server may have received the request.' : e.message);
      return null;
    } finally {
      clearTimeout(timer);
      canvas.inert = false;
      canvas.style.pointerEvents = '';
      // Unlocking can restore focus to the iframe. Keep the operation guarded
      // until focus is back on the host, so this is not mistaken for a new edit.
      if (aliveRef.current) operationFocusRef.current?.focus({ preventScroll: true });
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  // The SDK toolbar also emits onSave. Use the latest React fields, and persist
  // its native export directly without recursively asking the SDK to save again.
  nativeSaveRef.current = (jsonFile, htmlFile) => {
    try { exportFor('save', parseBeefreeExport(jsonFile, htmlFile)); }
    catch (e) { setError(e.message); }
  };

  const stay = () => {
    if (busyRef.current) return;
    pendingLeaveRef.current = null;
    setLeaveDestination(null);
  };
  const depart = (destination) => {
    // Only an explicit confirmation bypasses the browser guard, once. Reload,
    // tab close and browser Back continue to use native beforeunload protection.
    allowDepartureRef.current = true;
    try { window.location.assign(destination); }
    catch (_) {
      allowDepartureRef.current = false;
      setError('Could not open the destination. Keep editing and try again.');
    }
  };
  const saveAndLeave = async () => {
    const destination = pendingLeaveRef.current;
    if (!destination || busyRef.current) return;
    const savedId = await exportFor('save');
    if (savedId && aliveRef.current && !revisionsRef.current.dirty()) depart(destination);
  };

  const hasExport = exportRevision >= 0;
  return (
    <div className='bg-slate-50 min-h-screen'>
      <BeefreeLeaveDialog open={!!leaveDestination} busy={busy} canSave={ready} error={error} onStay={stay}
        onDiscard={() => { if (!busyRef.current && pendingLeaveRef.current) depart(pendingLeaveRef.current); }} onSave={saveAndLeave} />
      <div ref={operationFocusRef} tabIndex={-1} className='max-w-[1600px] mx-auto px-4 py-4'>
        <div className='flex items-center justify-between gap-4 mb-3'>
          <div className='flex items-center gap-3'>
            <a href='/sms_templates?channel=email' className='text-sm text-slate-500 hover:text-slate-800'>← Messages</a>
            <h1 className='text-xl font-semibold text-slate-900'>{id ? 'Edit email' : 'New email'} · Beefree</h1>
            {dirty && <span className='text-xs text-amber-600'>Save before leaving</span>}
          </div>
          <div className='flex items-center gap-2'>
            <TestSend templateId={id} senders={senders} dirty={dirty} beforeSend={() => exportFor('save')} disabled={busy || !ready} />
            <button type='button' disabled={!ready || busy} onClick={() => exportFor('save')} className='px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50'>{busy ? 'Working…' : 'Save template'}</button>
          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3 bg-white border border-slate-200 rounded-lg p-3 mb-3'>
          {[
            ['Template name *', name, setName, undefined],
            ['Subject *', subject, setSubject, 255],
            ['Preheader', preheader, setPreheader, 255]
          ].map(([label, value, update, maxLength]) => (
            <label key={label} className='text-xs font-medium text-slate-500'>{label}
              <input disabled={busy} className='mt-1 block w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900' value={value} maxLength={maxLength} onChange={(e) => { update(e.target.value); changed(); }} />
            </label>
          ))}
        </div>
        <div className='flex flex-wrap items-center justify-between gap-3 mb-3'>
          <div role='group' aria-label='Email view' className='inline-flex flex-wrap items-center gap-1 bg-slate-200/70 rounded-lg p-1'>
            {['visual', 'html', 'preview'].map((tab) => (
              <button key={tab} type='button' aria-pressed={mode === tab} disabled={busy || (tab !== 'visual' && !ready)} onClick={() => tab === 'visual' ? setMode('visual') : exportFor(tab)} className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${mode === tab ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}>
                {tab === 'visual' ? 'Visual editor' : tab === 'html' ? <><span>HTML</span><span className='rounded bg-slate-100 px-1.5 py-0.5 text-xs font-normal text-slate-500'>Read only</span></> : 'Preview'}
              </button>
            ))}
          </div>
          <span className={`text-sm ${hasExport && hasUnsubscribe(html) ? 'text-green-700' : 'text-slate-600'}`}>
            {!hasExport ? 'Save or preview to check the Unsubscribe link' : hasUnsubscribe(html) ? '✓ Last export: Unsubscribe link present' : '✕ Last export: no Unsubscribe link'}
          </span>
        </div>
        {notice && <div role='status' className='mb-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2'>{notice}</div>}
        {error && <div role='alert' className='mb-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2'>{error}</div>}
        <BeefreeAssetsPanel disabled={busy} />
        <div style={{ display: mode === 'visual' ? 'block' : 'none' }} className='relative border border-slate-200 rounded-lg overflow-hidden bg-white'>
          {!ready && !error && <div className='p-6 text-center text-slate-500'>Loading Beefree…</div>}
          <div id='retention-beefree-editor' ref={containerRef} style={{ height: 'calc(100vh - 300px)', minHeight: 580 }} />
          {busy && <div role='status' className='absolute inset-0 z-50 flex items-center justify-center bg-white/70 text-sm text-slate-700'>Preparing email…</div>}
        </div>
        {mode === 'html' && (
          <div>
            <p className='text-xs text-slate-500 mb-2'>Exported HTML is read only. Edit the visual design to keep the saved design and sent email consistent.</p>
            <textarea readOnly spellCheck={false} aria-label='Exported email HTML' value={html} className='w-full font-mono text-xs border border-slate-300 rounded-lg p-3 bg-slate-900 text-slate-100' style={{ height: 650 }} />
          </div>
        )}
        {mode === 'preview' && (
          <div className='bg-white border border-slate-200 rounded-lg p-4'>
            <div className='mb-3 flex justify-between gap-3'>
              <div><div className='text-sm font-semibold'>{subject || '(no subject)'}</div><div className='text-sm text-slate-500'>{preheader}</div></div>
              <div className='flex gap-2'>{['desktop', 'mobile'].map((size) => <button key={size} type='button' onClick={() => setDevice(size)} className={`text-sm px-3 py-1 rounded border ${device === size ? 'border-blue-300 text-blue-700' : 'border-slate-200'}`}>{size === 'desktop' ? 'Desktop' : 'Mobile'}</button>)}</div>
            </div>
            <PreviewFrame html={html} device={device} height={700} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BeefreeEmailEditor;
